import { useState } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { BloomHerLogo } from "@/components/BloomHerLogo";

export default function Auth({ mode }) {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const isSignup = mode === "signup";
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const endpoint = isSignup ? "/auth/register" : "/auth/login";
      const payload = isSignup ? form : { email: form.email, password: form.password };
      const { data } = await api.post(endpoint, payload);
      loginWithToken(data.token, data.user);
      toast.success(isSignup ? "Welcome to BloomHer!" : "Welcome back!");
      const member = data.user.role === "admin" || data.user.subscription_status === "active";
      navigate(member ? "/dashboard" : "/membership");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col items-center justify-center px-5 py-12">
      <Link to="/" className="flex items-center gap-2 mb-10" data-testid="auth-brand-logo">
        <BloomHerLogo size="text-3xl" />
      </Link>

      <div className="w-full max-w-md bg-white rounded-[2rem] border border-taupe shadow-[0_20px_40px_rgba(58,18,48,0.08)] p-8 sm:p-10">
        <h1 className="font-serif text-4xl text-wine tracking-tight text-center">
          {isSignup ? "Begin your ritual" : "Welcome back"}
        </h1>
        <p className="mt-2 text-center text-sm text-[#9B7E8D]">
          {isSignup ? "Create your account to join BloomHer." : "Sign in to continue your journey."}
        </p>

        <button
          data-testid="google-auth-btn"
          onClick={googleLogin}
          className="mt-8 flex w-full items-center justify-center gap-3 rounded-full border border-[#D8C2CD] bg-white px-6 py-3.5 text-sm font-medium text-[#3A1230] transition-all hover:bg-cream"
        >
          <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="h-5 w-5" />
          Continue with Google
        </button>

        <div className="my-6 flex items-center gap-4">
          <div className="h-px flex-1 bg-taupe" />
          <span className="text-xs uppercase tracking-[0.15em] text-[#9B7E8D]">or</span>
          <div className="h-px flex-1 bg-taupe" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          {isSignup && (
            <div>
              <label className="block text-sm font-medium text-[#6E5862] mb-2">Name</label>
              <input
                data-testid="auth-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full rounded-xl border border-[#D8C2CD] bg-white px-4 py-3 text-base text-[#3A1230] placeholder-[#9B7E8D] focus:border-wine focus:outline-none focus:ring-1 focus:ring-wine"
                placeholder="Your name"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-[#6E5862] mb-2">Email</label>
            <input
              data-testid="auth-email-input"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full rounded-xl border border-[#D8C2CD] bg-white px-4 py-3 text-base text-[#3A1230] placeholder-[#9B7E8D] focus:border-wine focus:outline-none focus:ring-1 focus:ring-wine"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#6E5862] mb-2">Password</label>
            <input
              data-testid="auth-password-input"
              type="password"
              required
              minLength={6}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full rounded-xl border border-[#D8C2CD] bg-white px-4 py-3 text-base text-[#3A1230] placeholder-[#9B7E8D] focus:border-wine focus:outline-none focus:ring-1 focus:ring-wine"
              placeholder="••••••••"
            />
          </div>
          <button
            data-testid="auth-submit-btn"
            disabled={busy}
            className="flex w-full items-center justify-center rounded-full bg-wine px-8 py-3.5 text-sm font-medium text-white transition-all hover:bg-wine-dark disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : isSignup ? "Create Account" : "Sign In"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-[#6E5862]">
          {isSignup ? "Already a member?" : "New to BloomHer?"}{" "}
          <Link
            data-testid="auth-switch-link"
            to={isSignup ? "/login" : "/signup"}
            className="font-medium text-wine hover:underline"
          >
            {isSignup ? "Sign in" : "Create an account"}
          </Link>
        </p>
      </div>
    </div>
  );
}
